<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">運費規則</h1>
      </div>
    </div>

    <div class="page-container commerce-shipping-page">
    <div class="commerce-rule-stack">
      <div class="settings-card">
        <div class="settings-card-header">
          <i class="ri-truck-line" style="color: #667eea;"></i>
          <div>
            <h2 class="settings-card-title">運費規則</h2>
            <p class="settings-card-desc">
              <template v-if="ecpayLogistics.enabled">
                綠界物流的寄送方式由系統帶入。可調整運費、順序與啟用狀態。
              </template>
              <template v-else>
                拖曳列首把手可調整順序。順序會用在運費試算。
              </template>
            </p>
          </div>
        </div>
        <div v-if="loadError" class="commerce-inline-alert alert alert-danger" role="alert">{{ loadError }}</div>
        <div v-if="ecpayLogistics.needsLogisticsType" class="commerce-inline-alert alert alert-warning" role="status">
          請先到
          <router-link :to="{ name: 'ClientCommerceSettings' }">商城設定</router-link>
          儲存綠界的物流類型，再回來建立寄送方式。
        </div>
        <div v-else-if="ecpayLogistics.typeMismatch" class="commerce-inline-alert alert alert-warning" role="status">
          目前物流類型與寄送方式不一致，請按「依目前物流類型重設」。
        </div>
        <div v-if="ecpayLogistics.enabled && ecpayLogistics.mode" class="shipping-ecpay-toolbar">
          <button type="button" class="btn btn-secondary" :disabled="saving" @click="resetEcpayShipping">
            依目前物流類型重設
          </button>
        </div>
        <div class="shipping-card-body">
          <div class="shipping-table-scroll">
            <table class="shipping-table">
              <thead>
                <tr>
                  <th scope="col" class="shipping-table__drag"></th>
                  <th scope="col">名稱</th>
                  <th scope="col">類型</th>
                  <th scope="col">設定</th>
                  <th scope="col" class="shipping-table__col-flag">啟用</th>
                  <th scope="col" class="shipping-table__col-actions">操作</th>
                </tr>
              </thead>
              <tbody>
                <template v-if="shippingRules.length === 0">
                  <tr>
                    <td colspan="6" class="shipping-table__empty">
                      {{ emptyShippingMessage }}
                    </td>
                  </tr>
                </template>
                <template v-else>
                  <tr
                    v-for="(r, index) in shippingRules"
                    :key="r.id"
                    :class="{
                      'shipping-table__row--editing': editingShippingId === r.id,
                      'shipping-table__row--dragging': shippingDragIndex === index,
                      'shipping-table__row--drag-over': shippingDragOverIndex === index,
                    }"
                    @dragover.prevent="onShippingDragOver(index)"
                    @drop="onShippingDrop(index)"
                  >
                    <td class="shipping-table__drag">
                      <button
                        type="button"
                        class="shipping-drag-handle"
                        draggable="true"
                        :disabled="saving"
                        aria-label="拖曳排序"
                        @dragstart="onShippingDragStart(index, $event)"
                        @dragend="onShippingDragEnd"
                      >
                        <i class="ri-drag-move-2-line" aria-hidden="true"></i>
                      </button>
                    </td>
                    <td class="shipping-table__cell-name">{{ r.name }}</td>
                    <td>{{ shippingRuleTypeLabel(r.rule_type) }}</td>
                    <td class="shipping-table__cell-config">{{ shippingRuleConfigSummary(r) }}</td>
                    <td class="shipping-table__col-flag">
                      <span class="shipping-badge" :class="r.is_active ? 'shipping-badge--on' : 'shipping-badge--off'">
                        {{ r.is_active ? '啟用' : '停用' }}
                      </span>
                    </td>
                    <td class="shipping-table__col-actions">
                      <div class="shipping-row-actions">
                        <button type="button" class="btn-shipping-edit" @click="startEditShipping(r)">編輯</button>
                        <button v-if="!isEcpayManaged(r)" type="button" class="btn-shipping-delete" @click="removeShipping(r.id)">刪除</button>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <section
            v-if="showShippingForm"
            class="shipping-add-panel"
            :class="{ 'shipping-add-panel--edit': editingShippingId }"
            aria-labelledby="shipping-add-title"
          >
            <h3 id="shipping-add-title" class="shipping-add-panel__title">
              {{ editingShippingId ? (editingManaged ? '編輯寄送方式' : '編輯規則') : '新增規則' }}
            </h3>
            <div class="add-shipping-grid">
              <div class="field">
                <label class="field-label" for="ship-name">規則名稱</label>
                <input id="ship-name" v-model="shipForm.name" class="form-control" placeholder="例如：本島宅配" :disabled="editingManaged" />
              </div>
              <div class="field">
                <label class="field-label" for="ship-type">規則類型</label>
                <select id="ship-type" v-model="shipForm.rule_type" class="form-control" :disabled="editingManaged">
                  <option value="flat_rate">固定運費</option>
                  <option value="threshold_free">滿額免運</option>
                  <option value="volumetric">按重量（公斤）計費</option>
                  <option value="dimensional">按長寬高（積材）計費</option>
                  <option value="max_edge">依最大邊計費</option>
                </select>
              </div>
              <template v-if="shipForm.rule_type === 'flat_rate'">
                <div class="field">
                  <label class="field-label" for="ship-fee-flat">運費（元）</label>
                  <input
                    id="ship-fee-flat"
                    v-model="shipForm.fee"
                    type="number"
                    min="0"
                    step="1"
                    class="form-control"
                  />
                </div>
              </template>

              <template v-else-if="shipForm.rule_type === 'threshold_free'">
                <div class="field">
                  <label class="field-label" for="ship-threshold">免運門檻（元）</label>
                  <input
                    id="ship-threshold"
                    v-model.number="shipForm.threshold_amount"
                    type="number"
                    min="0"
                    step="1"
                    class="form-control"
                  />
                </div>
                <div class="field">
                  <label class="field-label" for="ship-fee-th">未達門檻運費（元）</label>
                  <input
                    id="ship-fee-th"
                    v-model.number="shipForm.fee"
                    type="number"
                    min="0"
                    step="1"
                    class="form-control"
                  />
                </div>
              </template>

              <template v-else-if="shipForm.rule_type === 'volumetric'">
                <div class="field">
                  <label class="field-label" for="ship-rate">每公斤費率（元）</label>
                  <input
                    id="ship-rate"
                    v-model.number="shipForm.rate_per_kg"
                    type="number"
                    min="0"
                    step="0.01"
                    class="form-control"
                  />
                </div>
              </template>

              <template v-else-if="shipForm.rule_type === 'dimensional'">
                <div class="field field--full">
                  <label class="field-label" for="ship-rate-m3">每立方公尺費率（元／m³）</label>
                  <input
                    id="ship-rate-m3"
                    v-model.number="shipForm.rate_per_m3"
                    type="number"
                    min="0"
                    step="0.01"
                    class="form-control"
                  />
                  <p class="field-hint">
                    以各規格「長×寬×高（公分）」換算體積（÷1,000,000 為 m³）× 數量後加總，再乘以費率。尺寸請在商品／規格維護；未填長寬高之列不計入積材。
                  </p>
                </div>
              </template>

              <template v-else-if="shipForm.rule_type === 'max_edge'">
                <div class="field field--full">
                  <label class="field-label" for="ship-rate-max-cm">每公分費率（元／cm，依最大邊）</label>
                  <input
                    id="ship-rate-max-cm"
                    v-model.number="shipForm.rate_per_cm"
                    type="number"
                    min="0"
                    step="0.01"
                    class="form-control"
                  />
                  <p class="field-hint">
                    各規格取長、寬、高（公分）之<strong>最大邊</strong>，乘以數量，再乘以費率後加總。尺寸請在商品／規格維護；三邊皆未填或皆為 0 之列不計費。
                  </p>
                </div>
              </template>

              <div class="field field--checkbox">
                <label class="field-checkbox" for="ship-active">
                  <input id="ship-active" v-model="shipForm.is_active" type="checkbox" class="field-checkbox-input" />
                  <span>{{ editingManaged ? '啟用此寄送方式' : '啟用此規則（試算與結帳會納入）' }}</span>
                </label>
              </div>
            </div>
            <div class="add-shipping-actions">
              <button
                v-if="editingShippingId"
                type="button"
                class="btn btn-secondary"
                :disabled="saving"
                @click="cancelEditShipping"
              >
                取消編輯
              </button>
              <button type="button" class="btn btn-primary" :disabled="saving" @click="saveShipping">
                {{ saving ? '處理中…' : editingShippingId ? '儲存變更' : '新增規則' }}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { usePageTitle } from '@/composables/usePageTitle.js'
import { useCommerceAdvancedRules } from '@/composables/useCommerceAdvancedRules.js'

usePageTitle('運費規則')

const SHIPPING_RULE_TYPE_LABELS = {
  flat_rate: '固定運費',
  threshold_free: '滿額免運',
  volumetric: '按重量（公斤）計費',
  dimensional: '按長寬高（積材）計費',
  max_edge: '依最大邊計費',
}

function shippingRuleTypeLabel(type) {
  return SHIPPING_RULE_TYPE_LABELS[type] || type || '—'
}

function parseConfig(config) {
  if (config == null) return {}
  if (typeof config === 'object') return config
  if (typeof config === 'string') {
    try {
      return JSON.parse(config)
    } catch {
      return {}
    }
  }
  return {}
}

function shippingRuleConfigSummary(rule) {
  const c = parseConfig(rule.config)
  const rt = rule.rule_type
  if (rt === 'flat_rate') {
    if (c.fee === null || c.fee === undefined || c.fee === '') return '未填運費'
    return `運費 ${Number(c.fee)} 元`
  }
  if (rt === 'threshold_free') {
    return `滿 ${Number(c.threshold_amount ?? 0)} 元免運；未達運費 ${Number(c.fee ?? 0)} 元`
  }
  if (rt === 'volumetric') return `每公斤 ${Number(c.rate_per_kg ?? 0)} 元`
  if (rt === 'dimensional') return `每 m³ ${Number(c.rate_per_m3 ?? 0)} 元（長×寬×高 cm）`
  if (rt === 'max_edge') return `最大邊每公分 ${Number(c.rate_per_cm ?? 0)} 元`
  if (Object.keys(c).length) return JSON.stringify(c)
  return '—'
}

const {
  loadError,
  saving,
  shippingRules,
  ecpayLogistics,
  shipForm,
  editingShippingId,
  reload,
  saveShipping,
  resetEcpayShipping,
  shippingDragIndex,
  shippingDragOverIndex,
  onShippingDragStart,
  onShippingDragOver,
  onShippingDragEnd,
  onShippingDrop,
  startEditShipping,
  cancelEditShipping,
  removeShipping,
} = useCommerceAdvancedRules()

function isEcpayManaged(rule) {
  const config = parseConfig(rule?.config)
  return config.managedBy === 'ecpay'
}

const editingManaged = computed(() => {
  const rule = shippingRules.value.find((item) => item.id === editingShippingId.value)
  return isEcpayManaged(rule)
})

const showShippingForm = computed(() => !ecpayLogistics.value.enabled || Boolean(editingShippingId.value))

const emptyShippingMessage = computed(() => {
  if (ecpayLogistics.value.needsLogisticsType) return '尚未儲存物流類型，無法帶入寄送方式。'
  if (ecpayLogistics.value.enabled) return '尚無寄送方式。'
  return '尚無運費規則，請於下方填寫並新增。'
})

onMounted(() => reload({ ensureEcpayShipping: true }))
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

/* settings-card 僅在 settings/Index.vue scoped 內定義，此頁自帶卡片樣式 */
.commerce-rule-stack {
  width: 100%;
  max-width: 100%;
}

.settings-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
}

.settings-card-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
  background: #f8f9ff;
  border-bottom: 1px solid #e9ecef;
}

.settings-card-header > i {
  font-size: 32px;
  margin-top: 2px;
  flex-shrink: 0;
}

.settings-card-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 4px;
  color: #2d3748;
}

.settings-card-desc {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
  line-height: 1.5;
}

.commerce-inline-alert {
  margin: 16px 24px;
}

.shipping-ecpay-toolbar {
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px 0;
}

.alert {
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
}

.alert-warning {
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fde68a;
}

.alert-danger {
  background: #fff5f5;
  color: #c53030;
  border: 1px solid #fed7d7;
}

.shipping-card-body {
  /* 標題列／錯誤提示與下方條列表格之間留白 */
  padding: 20px 24px 24px;
}

.shipping-table-scroll {
  overflow-x: auto;
  margin-bottom: 24px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fff;
  -webkit-overflow-scrolling: touch;
}

.shipping-table {
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;
  font-size: 13px;
}

.shipping-table th,
.shipping-table td {
  padding: 12px 14px;
  text-align: left;
  vertical-align: middle;
  border-bottom: 1px solid #eef0f3;
}

.shipping-table th {
  background: #eef1f5;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  text-transform: none;
  letter-spacing: normal;
  white-space: nowrap;
  border-bottom: 1px solid #dee2e6;
}

.shipping-table tbody tr:last-child td {
  border-bottom: none;
}

.shipping-table tbody tr:hover {
  background: #fafbff;
}

.shipping-table__col-num {
  text-align: right;
  width: 1%;
  white-space: nowrap;
}

.shipping-table__col-flag {
  text-align: center;
  width: 1%;
  white-space: nowrap;
}

.shipping-table__col-actions {
  text-align: right;
  width: 1%;
  white-space: nowrap;
}

.shipping-table thead .shipping-table__col-actions {
  text-align: center;
}

.shipping-table__row--editing td {
  background: #eef2ff;
}

.shipping-table tbody tr.shipping-table__row--editing:hover td {
  background: #eef2ff;
}

.shipping-row-actions {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
}

.btn-shipping-edit {
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  color: #4338ca;
  background: #fff;
  border: 1px solid #c7d2fe;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-shipping-edit:hover {
  background: #eef2ff;
}

.field--checkbox {
  grid-column: 1 / -1;
}

.field--full {
  grid-column: 1 / -1;
  align-self: start;
}

.field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.45;
}

.field-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
  line-height: 1.4;
}

.field-checkbox-input {
  margin-top: 2px;
  width: 16px;
  height: 16px;
  accent-color: #667eea;
  flex-shrink: 0;
  cursor: pointer;
}

.shipping-add-panel--edit {
  border-color: #c7d2fe;
  box-shadow: 0 0 0 1px rgba(102, 126, 234, 0.12);
}

.shipping-table__drag {
  width: 40px;
  text-align: center;
}

.shipping-drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  color: #9ca3af;
  background: transparent;
  border: 0;
  border-radius: 4px;
  cursor: grab;
  font-size: 18px;
}

.shipping-drag-handle:hover {
  color: #667eea;
}

.shipping-drag-handle:active {
  cursor: grabbing;
}

.shipping-drag-handle:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.shipping-table__row--dragging {
  opacity: 0.4;
}

.shipping-table__row--drag-over {
  outline: 2px solid #667eea;
  outline-offset: -2px;
}

.shipping-table__cell-name {
  font-weight: 500;
  color: #2d3748;
}

.shipping-table__cell-config {
  color: #4b5563;
  word-break: break-word;
}

.shipping-table__empty {
  text-align: center;
  color: #9ca3af;
  padding: 28px 16px !important;
}

.shipping-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
}

.shipping-badge--on {
  background: #d1fae5;
  color: #047857;
}

.shipping-badge--off {
  background: #f3f4f6;
  color: #6b7280;
}

.btn-shipping-delete {
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  color: #b91c1c;
  background: #fff;
  border: 1px solid #fecaca;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-shipping-delete:hover {
  background: #fef2f2;
}

.shipping-add-panel {
  /* 條列表格與「新增規則」區塊之間留白 */
  margin-top: 8px;
  padding: 20px;
  background: linear-gradient(180deg, #fafbff 0%, #f8f9fa 100%);
  border: 1px solid #e9ecef;
  border-radius: 10px;
}

.shipping-add-panel__title {
  margin: 0 0 16px;
  font-size: 15px;
  font-weight: 600;
  color: #2d3748;
}

.add-shipping-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px 18px;
  align-items: end;
  margin-bottom: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.field-label {
  font-size: 12px;
  color: #4b5563;
  font-weight: 600;
}

.add-shipping-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  padding-top: 4px;
  border-top: 1px solid #e9ecef;
}
</style>
